import React from "react";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider } from "./../../../hooks/hook-form";
import { Input } from "./../../../components";
import { LoadingButton } from "@mui/lab";
import { useAuth } from "../../../hooks";
import { en, ar, fr, messagera } from "./../../../assets";
import {
  enqueueSnackbar as enqueueSnackbarAction,
  closeSnackbar as closeSnackbarAction,
} from "../../../slices/notifier";
import { useDispatch } from "react-redux";
import { Button } from "@mui/material";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useTranslation } from "react-i18next";

const Login = () => {
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();
  const enqueueSnackbar = (...args) => dispatch(enqueueSnackbarAction(...args));
  const closeSnackbar = (...args) => dispatch(closeSnackbarAction(...args));
  const showSnackbar = (data) => {
    // NOTE:
    // if you want to be able to dispatch a `closeSnackbar` action later on,
    // you SHOULD pass your own `key` in the options. `key` can be any sequence
    // of number or characters, but it has to be unique for a given snackbar.
    enqueueSnackbar({
      message: t(data.message),
      options: {
        key: new Date().getTime() + Math.random(),
        variant: data.variant,
        action: (key) => (
          <Button style={{ color: "white" }} onClick={() => closeSnackbar(key)}>
            {t("dismiss me")}
          </Button>
        ),
      },
    });
  };

  const LoginSchema = Yup.object().shape({
    email: Yup.string()
      .email(t("Email must be a valid email address"))
      .required(t("Email is required")),
    password: Yup.string()
      .required(t("Password is required"))
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$@!%&*?])[A-Za-z\d#$@!%&*?]{8,30}$/,
        t(
          "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character"
        )
      ),
  });
  const defaultValues = {
    email: "",
    password: "",
  };
  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });
  const {
    handleSubmit,
    clearErrors,
    formState: { isSubmitting },
    register,
  } = methods;
  const { login } = useAuth();
  const onSubmit = async (values) => {
    try {
      await login(values.email, values.password);
      showSnackbar({ variant: "success", message: t("Logged in") });
    } catch (err) {
      showSnackbar({ variant: "error", message: t(err.message) });
    }
  };
  const LANGS = [
    {
      value: "en",
      label: "English",
      icon: en,
    },
    {
      value: "ar",
      label: "العربية",
      icon: ar,
    },
    {
      value: "fr",
      label: "French",
      icon: fr,
    },
  ];
  const handleClick = (flag) => {
    i18n.changeLanguage(flag);
    clearErrors();
  };
  return (
    <div id="login-container">
      <div className="box-form">
        <div className="right">
          <div className="login-img-text-wrapper">
            <div className="messaggera-logo">
              <LazyLoadImage src={messagera} alt="takiLogo" id="takilogo" />
            </div>

            <div className="text-wrapper">
              <h2>{t("Welcome To Operator View")}</h2>
              <span className="login-description">
                {t(`An account is required. Please ask an existing member for`)}
              </span>
              {!i18n.language.includes("fr") && <br />}
              <span className="login-description">
                {t(`an invite or log in to continue.`)}
              </span>
            </div>
          </div>
          <FormProvider
            methods={methods}
            onSubmit={handleSubmit(onSubmit)}
            className={"login-form"}
          >
            <Input
              name="email"
              id="login"
              label={t("Email")}
              className={"email"}
              placeholder={t("Enter your Email")}
              errors={methods.formState.errors}
              register={register}
            />
            <Input
              name="password"
              label={t("Password")}
              id="login"
              placeholder={t("Enter your password")}
              className="password"
              errors={methods.formState.errors}
              register={register}
            />
            <div className="flags">
              {LANGS.map((flag) => {
                return (
                  <div key={flag.value}>
                    <LazyLoadImage
                      src={flag.icon}
                      onClick={() => handleClick(flag.value)}
                      alt={"flag.icon"}
                    />
                  </div>
                );
              })}
            </div>
            <LoadingButton
              size="large"
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              {t("Login")}
            </LoadingButton>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default Login;
